import { useIntl } from 'react-intl'

import { PokerCardsIcon } from '@gto/icons-react/outline/poker-cards'
import { cn } from '@gto/ui/cn'
import { ClubSolidIcon } from '@gto/ui/icons/club-solid-icon'
import { SpadeSolidIcon } from '@gto/ui/icons/spade-solid-icon'
import { Tooltip } from '@gto/ui/tooltip'

import { type ParsedPlanSegment, parsePlanName, type TierToken } from './parse-plan-name'

/*
 * Rendering: each grammar position gets a distinct, compact treatment so the
 * cell reads on ONE line (instead of four identical wrapping chips):
 *   game    → small suit icon + name
 *   format  → neutral chip
 *   tier    → colour-coded chip (the one splash of colour)
 *   billing → muted text
 *
 * Tier tints are theme-aware `-main` semantic tokens (the same soft-tint pattern
 * as the impersonation banner — `bg-<hue>-main/15 text-<hue>-main`), so the ramp
 * holds in light and dark: neutral grey → blue → brand-green → gold as the tier
 * rises (error-red is skipped — it would read as "broken").
 */

// A small suit icon marks the game at a glance; unknown games get a generic
// poker-cards glyph. Icons inherit colour via currentColor, so they tint with
// the surrounding muted text. Intentionally open (any game token → fallback).
const GAME_ICON: Record<string, typeof SpadeSolidIcon> = {
  NLHOLDEM: SpadeSolidIcon,
  PLO4: ClubSolidIcon,
}

const TIER_PILL_CLASS: Record<TierToken, string> = {
  STARTER: 'bg-secondary-main text-text-secondary', // base tier — neutral
  PREMIUM: 'bg-info-main/15 text-info-main',
  ELITE: 'bg-primary-main/15 text-primary-main',
  ULTRA: 'bg-warning-main/15 text-warning-main',
}

type PlanNameProps = {
  value: string | null | undefined
}

export const PlanName = ({ value }: PlanNameProps) => {
  const { formatMessage } = useIntl()
  const raw = typeof value === 'string' ? value : ''
  const segments = parsePlanName(value)

  if (segments.length === 0) {
    return <span aria-label={formatMessage({ id: 'planName.empty' })}>—</span>
  }

  // Resolve each label from the message catalog when the segment is mapped;
  // unknown tokens keep their title-cased fallback so they still render, just
  // untranslated.
  const labelFor = (segment: ParsedPlanSegment): string => {
    if (segment.kind === 'unknown') {
      return segment.label
    }

    return formatMessage({
      id: `plan.${segment.kind}.${segment.raw}`,
      defaultMessage: segment.label,
    })
  }

  // Each grammar position renders distinctly: game = suit icon + name, format =
  // neutral chip, tier = colour-coded chip, billing = muted text. The grammar
  // yields at most one of each, so `find` is sufficient; unrecognised tokens
  // trail as muted text rather than vanishing.
  const game = segments.find((s) => s.kind === 'game')
  const format = segments.find((s) => s.kind === 'format')
  const tier = segments.find((s) => s.kind === 'tier')
  const billing = segments.find((s) => s.kind === 'billing')
  const extras = segments.filter((s) => s.kind === 'unknown')
  const GameIcon = game === undefined ? null : (GAME_ICON[game.raw] ?? PokerCardsIcon)

  // The DataGrid cell forces line-height: 44px onto its content; this preset has
  // no `leading-none` utility, so chips would inherit that and balloon into tall
  // ovals. `leading-100` (the preset's line-height: 100% token) pins the chip
  // line box back to the text, and `h-[1.125rem]` gives a consistent 18px pill
  // regardless of the inherited cell metrics.
  const PILL =
    'inline-flex h-[1.125rem] shrink-0 items-center rounded-full px-2 text-xs font-medium leading-100'

  return (
    <Tooltip content={raw}>
      <div className="flex w-full min-w-0 items-center gap-1.5 leading-100">
        {game !== undefined && (
          <span className="flex shrink-0 items-center gap-1">
            {GameIcon !== null && (
              <GameIcon
                aria-hidden
                className="size-3.5 shrink-0 text-text-secondary"
              />
            )}
            <span className="whitespace-nowrap text-sm text-text-primary">{labelFor(game)}</span>
          </span>
        )}
        {format !== undefined && (
          // The only variable-length segment — it (not the game name or the tier
          // chip) absorbs a narrow cell by truncating, so the short, meaningful
          // bits stay whole. Full text remains in the row tooltip.
          <span className={cn(PILL, 'min-w-0 shrink bg-secondary-main text-text-secondary')}>
            <span className="min-w-0 truncate">{labelFor(format)}</span>
          </span>
        )}
        {tier !== undefined && (
          // raw is a TierToken whenever kind === 'tier' (the parser only tags it
          // via TIER_TOKEN_LABEL), so the typed map always yields a colour.
          <span
            className={cn(PILL, TIER_PILL_CLASS[tier.raw as TierToken])}
            data-tst="plan-tier"
          >
            {labelFor(tier)}
          </span>
        )}
        {billing !== undefined && (
          <span className="shrink-0 text-xs text-text-secondary">{labelFor(billing)}</span>
        )}
        {extras.length > 0 && (
          <span className="truncate text-sm text-text-secondary">
            {extras.map(labelFor).join(' · ')}
          </span>
        )}
      </div>
    </Tooltip>
  )
}
